<template>
  <el-drawer
    title="批量导入国际化语言"
    :visible.sync="dialogVisible"
    :wrapperClosable="false"
    direction="rtl"
    size="580px"
    append-to-body
    @close="resetForm"
  >
    <div class="drawer-body">
      <div class="import-tip">
        <div class="tip-title">提示</div>
        <ol class="tip-list">
          <li>1.列的顺序请严格按照模板文件排列</li>
          <li>2.文件格式须为xlsx、xls格式</li>
          <li>
            3.导入模板下载：<el-button
              type="text"
              class="download-link"
              @click="downloadTemplate"
              >导入模板.xlsx</el-button
            >
          </li>
        </ol>
      </div>
      <div class="upload-row">
        <span class="upload-label"
          ><span class="required">*</span>上传Excel文件：</span
        >
        <el-upload
          action="#"
          :show-file-list="false"
          :auto-upload="false"
          accept=".xlsx,.xls"
          :on-change="handleFileChange"
        >
          <el-button type="primary" icon="el-icon-upload2">上传文件</el-button>
        </el-upload>
      </div>
      <div v-if="fileName" class="file-name">{{ fileName }}</div>
    </div>
    <div class="drawer-footer">
      <el-button type="primary" @click="handleConfirm" :loading="submitting"
        >确定</el-button
      >
      <el-button @click="dialogVisible = false">取消</el-button>
    </div>
  </el-drawer>
</template>

<script>
import XLSX from "xlsx";
import { mapGetters } from "vuex";
import { batchInsertAssemblyDesc } from "@/api/componentManage";

export default {
  props: {
    isShow: { type: Boolean, default: false },
    assemblyId: { type: [String, Number], default: "" },
    // 当前组件已存在的国际化语言列表（完整列表）
    existingList: { type: Array, default: () => [] },
  },
  computed: {
    ...mapGetters(["languageList"]),
    dialogVisible: {
      get() {
        return this.isShow;
      },
      set(val) {
        this.$emit("update:isShow", val);
      },
    },
  },
  data() {
    return {
      fileName: "",
      fileHeader: [], // 文件列头，用于列结构校验
      parsedRows: null, // [{ languageName, assemblyNameLang }]
      submitting: false,
    };
  },
  watch: {
    dialogVisible(val) {
      if (val) {
        this.resetForm();
        // 确保语言列表已加载
        this.$store.dispatch("updateLanguageList");
      }
    },
  },
  methods: {
    resetForm() {
      this.fileName = "";
      this.fileHeader = [];
      this.parsedRows = null;
      this.submitting = false;
    },
    /**
     * 下载模板：A列预填系统支持的全部语言（与添加语言下拉一致），B列为空
     */
    async downloadTemplate() {
      // 确保语言列表已加载
      let langs = this.languageList;
      if (!langs || !langs.length) {
        langs = await this.$store.dispatch("updateLanguageList");
      }
      if (!langs || !langs.length) {
        this.$message.warning("语言列表加载失败，请稍后重试");
        return;
      }
      const header = ["语言", "翻译名称"];
      const data = [header];
      langs.forEach((item) => {
        data.push([item.languageName, ""]);
      });
      const sheet = XLSX.utils.aoa_to_sheet(data);
      sheet["!cols"] = [{ wch: 20 }, { wch: 30 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "sheet1");
      XLSX.writeFile(wb, "导入模板.xlsx");
    },
    /**
     * 文件选择（不自动上传，仅本地读取）
     * 解析完成后循环匹配语言 key，过滤掉未填写的行和未匹配到国家的行
     */
    async handleFileChange(file) {
      if (!file) return;
      const rawFile = file.raw || file;
      const name = file.name || rawFile.name || "";
      const ext = name.substring(name.lastIndexOf(".") + 1).toLowerCase();
      if (ext !== "xlsx" && ext !== "xls") {
        this.$message.error("文件格式须为xlsx、xls格式");
        return;
      }
      this.fileName = name;
      // 读取前确保语言列表已加载（用于匹配 languageType）
      if (!this.languageList || !this.languageList.length) {
        await this.$store.dispatch("updateLanguageList");
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const firstSheetName = wb.SheetNames[0];
          const sheet = wb.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            blankrows: false,
          });
          const { header, list } = this.buildRows(rows);
          this.fileHeader = header;
          this.parsedRows = list;

          console.log(this.fileHeader, this.parsedRows, "@@");
        } catch (err) {
          console.error(err);
          this.fileHeader = [];
          this.parsedRows = null;
          this.$message.error("文件解析失败，请检查文件内容");
        }
      };
      reader.readAsArrayBuffer(rawFile);
    },
    /**
     * 解析二维数组：
     * - header：第一行列头（用于列结构校验）
     * - list：从第二行开始的原始数据行（不过滤，校验留到提交时）
     */
    buildRows(rows) {
      if (!rows || !rows.length) return { header: [], list: [] };
      const header = (rows[0] || []).map((v) => String(v || "").trim());
      const list = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i] || [];
        const languageName = String(row[0] == null ? "" : row[0]).trim();
        const assemblyNameLang = String(row[1] == null ? "" : row[1]).trim();
        // 跳过完全空白行
        if (!languageName && !assemblyNameLang) continue;
        list.push({ languageName, assemblyNameLang });
      }
      return { header, list };
    },
    /**
     * 校验 + 提交，校验顺序：
     * 1. 未上传文件 → 请先上传文件
     * 2. 列结构：A=语言、B=翻译名称 → 文件格式不符
     * 3. 内容缺失：A有值但B为空 → 部分语言翻译内容为空
     * 4. 文件内重复语言 → 文件中存在重复语言
     * 5. 与已有语言冲突 → 弹窗跳过/覆盖
     */
    async handleConfirm() {
      // 1. 未上传文件或解析失败
      if (!this.fileName || !this.parsedRows) {
        this.$message.warning("请先上传文件");
        return;
      }
      // 2. 列结构校验
      if (this.fileHeader[0] !== "语言" || this.fileHeader[1] !== "翻译名称") {
        this.$message.warning("文件格式不符，请重新提交");
        return;
      }
      const rawList = this.parsedRows;
      if (!rawList.length) {
        this.$message.warning("请先上传文件");
        return;
      }
      // 3. 内容缺失：A 有值但 B 为空
      const hasMissing = rawList.some(
        (item) => item.languageName && !item.assemblyNameLang,
      );
      if (hasMissing) {
        this.$message.warning("部分语言翻译内容为空，请重新提交");
        return;
      }
      // 建立语言名 -> languageType 映射，循环匹配 key，过滤掉未匹配到国家的行
      const nameToType = {};
      (this.languageList || []).forEach((l) => {
        nameToType[l.languageName] = l.languageType;
      });
      const importList = [];
      rawList.forEach((item) => {
        if (!item.languageName || !item.assemblyNameLang) return;
        const language = nameToType[item.languageName];
        if (!language) return;
        importList.push({
          language,
          languageName: item.languageName,
          assemblyNameLang: item.assemblyNameLang,
        });
      });
      if (!importList.length) {
        this.$message.warning("没有可导入的有效数据，请检查文件");
        return;
      }
      // 4. 文件内重复语言校验
      const langCount = {};
      importList.forEach((item) => {
        langCount[item.language] = (langCount[item.language] || 0) + 1;
      });
      const duplicateInFile = Object.keys(langCount).some(
        (key) => langCount[key] > 1,
      );
      if (duplicateInFile) {
        this.$message.warning("文件中存在重复语言，请重新提交");
        return;
      }
      // 5. 与已有语言冲突校验
      const existMap = {};
      (this.existingList || []).forEach((item) => {
        existMap[item.language] = item;
      });
      const conflictItems = importList.filter(
        (item) => existMap[item.language],
      );
      if (conflictItems.length) {
        const conflictNames = conflictItems
          .map((item) => item.languageName)
          .join("、");
        try {
          const action = await this.confirmConflict(conflictNames);
          if (action === "skip") {
            // 跳过：仅导入未存在的语言（后端根据 importType=1 跳过已存在）
            await this.submitImport(importList, 1);
          } else if (action === "cover") {
            // 覆盖：全量导入，已有语言更新翻译内容
            await this.submitImport(importList, 2);
          }
        } catch (e) {
          // 取消操作
        }
      } else {
        await this.submitImport(importList, 1);
      }
    },
    /**
     * 弹出冲突选择框，返回 'skip' | 'cover'
     */
    confirmConflict(conflictNames) {
      return new Promise((resolve, reject) => {
        this.$msgbox({
          title: "提示",
          message: `以下语言已存在：${conflictNames}`,
          showCancelButton: true,
          confirmButtonText: "覆盖",
          cancelButtonText: "跳过",
          distinguishCancelAndClose: true,
          type: "warning",
        })
          .then(() => resolve("cover"))
          .catch((action) => {
            if (action === "cancel") {
              resolve("skip");
            } else {
              reject(action);
            }
          });
      });
    },
    /**
     * 调用批量接口 /assembly/batchInsertAssemblyDesc
     * @param {Array} list 待导入列表 [{ language, assemblyNameLang }]
     * @param {Number} importType 1=跳过已有语言 2=覆盖已有语言
     */
    async submitImport(list, importType) {
      if (!list || !list.length) {
        this.$message.info("没有可导入的数据");
        return;
      }
      this.submitting = true;
      try {
        const res = await batchInsertAssemblyDesc({
          assemblyId: this.assemblyId,
          importType,
          descList: list.map((item) => ({
            language: item.language,
            assemblyNameLang: item.assemblyNameLang,
          })),
        });
        // 响应拦截器在异常时会返回 false，需要判断避免误报成功
        if (res === false || res == null) {
          return;
        }
        this.$message.success("批量导入成功");
        this.dialogVisible = false;
        this.$emit("refresh");
      } catch (err) {
        console.error(err);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.drawer-body {
  padding: 0 20px 20px;
}

.drawer-footer {
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}

.import-tip {
  background: #fff;

  .tip-title {
    font-weight: 600;
    margin-bottom: 6px;
  }

  .tip-list {
    padding-left: 20px;
    margin: 0 0 16px 0;
    line-height: 26px;
    color: #606266;
  }

  .download-link {
    padding: 0;
    margin-left: 4px;
  }
}

.upload-row {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .upload-label {
    width: 140px;
    text-align: right;
    margin-right: 8px;
    color: #606266;
  }

  .required {
    color: #f56c6c;
    margin-right: 4px;
  }
}

.file-name {
  margin-left: 128px;
  margin-top: 8px;
  color: #606266;
  word-break: break-all;
}
</style>
